import { useState } from "react";
import { CheckCircle2, Link2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { AJUDA_PROTESE } from "@/lib/ajuda/dialogos/protese";
import { useOwnedOrganizations } from "@/lib/owned-organizations";
import { env } from "@/lib/env";
import { ResultStage, formatExpiry } from "@/components/shared/InviteResultStage";

type Props = {
  open: boolean;
  onClose: () => void;
};

type Stage =
  | { kind: "search" }
  | { kind: "invite-form" }
  | { kind: "invite-result"; token: string; expiresAt: Date | null };

/** "Novo laboratório" — só conecta a laboratórios reais: busca entre organizações já na
 *  plataforma, ou (se não achar) gera um convite por link que só vira registro no aceite. */
export function ProsthesisLabAddDialog({ open, onClose }: Props) {
  const utils = trpc.useUtils();
  const [stage, setStage] = useState<Stage>({ kind: "search" });
  const [search, setSearch] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const searchQuery = trpc.prosthesis.lab.search.useQuery(
    { search: search.trim() },
    { enabled: search.trim().length >= 2 },
  );
  const results = searchQuery.data ?? [];

  const { clinics } = useOwnedOrganizations();
  const [todasAsClinicas, setTodasAsClinicas] = useState(false);
  const varias = clinics.length > 1;

  const connectExisting = trpc.prosthesis.lab.connectExisting.useMutation({
    onSuccess: (r) => {
      const enviados = r?.enviados ?? [];
      const pulados = [...(r?.jaConectadas ?? []), ...(r?.jaPendentes ?? [])];
      toast.success(
        enviados.length > 1
          ? `Pedido enviado por ${enviados.length} clínicas — aguardando o laboratório aceitar.`
          : "Pedido de conexão enviado — aguardando o laboratório aceitar.",
        pulados.length > 0
          ? { description: `Sem pedido pra ${pulados.join(", ")} — já conectada ou com pedido pendente.` }
          : undefined,
      );
      utils.prosthesis.lab.connectionRequests.listOutgoing.invalidate();
      handleClose();
    },
    onError: (err) => toast.error(err.message || "Erro ao enviar pedido de conexão."),
  });

  const createInvite = trpc.prosthesis.lab.connectInvite.create.useMutation({
    onSuccess: (invite) => {
      setStage({
        kind: "invite-result",
        token: invite.token,
        expiresAt: invite.expiresAt ? new Date(invite.expiresAt) : null,
      });
    },
    onError: (err) => toast.error(err.message || "Erro ao gerar convite."),
  });

  function handleClose() {
    onClose();
    setTimeout(() => {
      setStage({ kind: "search" });
      setSearch("");
      setDisplayName("");
      setCopied(false);
      setShowQr(false);
    }, 250);
  }

  const inviteUrl = stage.kind === "invite-result" ? `${env.landingUrl}/conectar-laboratorio/${stage.token}` : "";
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`Olá! Conecte o laboratório de vocês ao DentalBook: ${inviteUrl}`)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success("Link copiado.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const expiryLabel = stage.kind === "invite-result" && stage.expiresAt ? formatExpiry(stage.expiresAt) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-md dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle
            className="dark:text-foreground"
            ajuda={stage.kind === "invite-result" ? AJUDA_PROTESE.conviteDeLaboratorioCriado : AJUDA_PROTESE.novoLaboratorio}
          >
            {stage.kind === "invite-result" ? "Convite criado" : "Novo laboratório"}
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {stage.kind === "search"
              ? "Busque um laboratório já cadastrado na plataforma pelo nome."
              : stage.kind === "invite-form"
                ? "Não encontrou? Convide por link — a conexão só vira real quando o laboratório aceitar."
                : "Compartilhe o link com o laboratório."}
          </DialogDescription>
        </DialogHeader>

        {stage.kind === "search" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome do laboratório..."
                className="h-9 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
              />
            </div>

            {search.trim().length >= 2 && (
              <div className="max-h-64 space-y-1.5 overflow-y-auto">
                {searchQuery.isFetching ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
                  </div>
                ) : results.length === 0 ? (
                  <p className="py-2 text-center text-xs text-zinc-500 dark:text-muted-foreground">
                    Nenhum laboratório encontrado com esse nome.
                  </p>
                ) : (
                  results.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between gap-2 rounded-lg border border-zinc-200 px-3 py-2 dark:border-border"
                    >
                      <span className="truncate text-sm font-medium text-zinc-900 dark:text-foreground">
                        {r.name}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          connectExisting.mutate({
                            organizationId: r.id,
                            todasAsMinhasClinicas: varias && todasAsClinicas,
                          })
                        }
                        disabled={connectExisting.isPending}
                        className="inline-flex h-8 shrink-0 items-center gap-1 rounded-md bg-brand-600 px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
                      >
                        {connectExisting.isPending ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                        Solicitar conexão
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {varias && search.trim().length >= 2 && results.length > 0 && (
              <label className="flex items-start gap-2 rounded-lg border border-zinc-200 px-3 py-2 dark:border-border">
                <input
                  type="checkbox"
                  checked={todasAsClinicas}
                  onChange={(e) => setTodasAsClinicas(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
                />
                <span className="min-w-0">
                  <span className="block text-[12px] font-medium text-zinc-800 dark:text-foreground">
                    Solicitar por todas as minhas clínicas ({clinics.length})
                  </span>
                  <span className="block text-[11px] text-zinc-500 dark:text-muted-foreground">
                    {clinics.map((c) => c.name).join(", ")} — o laboratório recebe uma solicitação por
                    clínica e aceita uma a uma.
                  </span>
                </span>
              </label>
            )}

            <button
              type="button"
              onClick={() => {
                setDisplayName(search.trim());
                setStage({ kind: "invite-form" });
              }}
              className="inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted"
            >
              Não encontrou? Convide por link
            </button>
          </div>
        )}

        {stage.kind === "invite-form" && (
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">
                Nome do laboratório
              </label>
              <input
                autoFocus
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ex: Laboratório Sorriso"
                className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setStage({ kind: "search" })}
                className="inline-flex h-9 items-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={() => {
                  if (displayName.trim().length < 2) {
                    toast.error("Informe o nome do laboratório.");
                    return;
                  }
                  createInvite.mutate({ labDisplayName: displayName.trim() });
                }}
                disabled={createInvite.isPending}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
              >
                {createInvite.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Link2 className="h-3.5 w-3.5" />
                )}
                Gerar link
              </button>
            </div>
          </div>
        )}

        {stage.kind === "invite-result" && (
          <ResultStage
            inviteUrl={inviteUrl}
            phone={null}
            email={null}
            whatsappHref={whatsappHref}
            mailtoHref=""
            copied={copied}
            onCopy={copy}
            showQr={showQr}
            onToggleQr={() => setShowQr((v) => !v)}
            expiryLabel={expiryLabel}
            onNewLink={() => setStage({ kind: "invite-form" })}
            onClose={handleClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
